import React, { useContext, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../AuthContext';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { authState, clientLoginformData } = useContext(AuthContext);
  const navigate = useNavigate();

  if (authState?.token) {
    return <Navigate to="/" replace />;
  }

  // ✅ Password validation rules
  const validatePassword = password => {
    const minLength = /.{8,}/;
    const upperCase = /[A-Z]/;
    const lowerCase = /[a-z]/;
    const number = /[0-9]/;
    const specialChar = /[!@#$%^&*(),.?":{}|<>]/;

    if (!minLength.test(password)) return '❌ Must be at least 8 characters long.';
    if (!upperCase.test(password)) return '❌ Must contain at least one uppercase letter.';
    if (!lowerCase.test(password)) return '❌ Must contain at least one lowercase letter.';
    if (!number.test(password)) return '❌ Must contain at least one number.';
    if (!specialChar.test(password)) return '❌ Must contain at least one special character.';

    return null;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setMessage('');

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setMessage(passwordError);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage('❌ New password and confirm password do not match!');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`/api/pass/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: formData.newPassword }),
      });

      const data = await res.json();

      if (res.ok) {
        setMessage('✅ Password has been reset successfully.');
        alert('Your Password has been reset successfully ✅');
        if (clientLoginformData?.slug) {
          return navigate(`/login/${clientLoginformData?.slug}`);
        } else {
          navigate('/login');
        }
      } else {
        setMessage(data.message || '❌ Something went wrong!');
      }
    } catch (err) {
      setMessage('⚠️ Server error, please try again later.');
    }

    setLoading(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div className="col-md-6 col-lg-5" style={{ justifySelf: 'center' }}>
          <img src="/vendors/images/login-page-img.png" alt="Holomedia" />
        </div>
        <h2 style={styles.title}>Reset Password</h2>
        <p style={styles.subtitle}>Create a new, strong password.</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* New Password */}

          <div style={styles.inputContainer}>
            <div>
              {' '}
              <label style={styles.label} htmlFor="newPassword">
                New Password
              </label>
            </div>
            <div style={styles.inputGroup}>
              <span style={styles.icon}>🔒</span>
              <input
                type="password"
                placeholder="Enter new password"
                style={styles.input}
                value={formData.newPassword}
                onChange={e => setFormData({ ...formData, newPassword: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Confirm Password */}
          <div style={styles.inputContainer}>
            <div>
              {' '}
              <label style={styles.label} htmlFor="confirmPassword">
                Conifrm Password
              </label>
            </div>
            <div style={styles.inputGroup}>
              <span style={styles.icon}>🔄</span>
              <input
                type="password"
                placeholder="Confirm new password"
                style={styles.input}
                value={formData.confirmPassword}
                onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
              />
            </div>
          </div>

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        {message && <p style={styles.message}>{message}</p>}
      </div>
    </div>
  );
}

// 🎨 Inline CSS
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#f9f9f9',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
  },
  card: {
    background: '#fff',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 6px 15px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '6px',
    color: '#222',
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '20px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  inputGroup: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    textAlign: 'left',
  },
  label: {
    fontSize: '14px',
    color: '#333',
    marginBottom: '2px',
    fontWeight: '600',
  },
  icon: {
    position: 'absolute',
    left: '12px',
    fontSize: '18px',
    color: '#666',
  },
  input: {
    width: '100%',
    padding: '12px 12px 12px 40px', // left padding for icon
    borderRadius: '6px',
    border: '1px solid #ccc',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  button: {
    padding: '8px',
    borderRadius: '6px',
    border: 'none',
    background: '#eb8d16',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.3s',
  },
  message: {
    marginTop: '15px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#444',
  },
  footerText: {
    marginTop: '20px',
    fontSize: '13px',
    color: '#666',
  },
};
